import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { fabric } from 'fabric';
import { v4 as uuidv4 } from 'uuid';

import { AddPhotosModal } from '@/components/common';
import { ColorPicker, Label, Toolbar } from '@/components/generic';
import {
  CANVAS_OBJECT_TYPE,
  DEFAULT_CANVAS_FONT_COLOR,
  DEFAULT_CANVAS_FONT_SIZE,
  DEFAULT_CANVAS_SIZE,
  DEFAULT_CANVAS_TEXTBOX_WIDTH,
  fonts
} from '@/constants';
import { ALIGNMENT, POSITION } from '@/enums';
import {
  checkIsCanvasEmpty,
  getCanvasFromRef,
  getCanvasImageScale,
  setCanvasBackgroundImage
} from '@/helpers';
import {
  CanvasTextIcon,
  DeleteIcon,
  DoubleCursorIcon,
  GalleryAddIcon,
  ImageIcon,
  PhotoFilmIcon,
  TextAddIcon
} from '@/resources';
import { Asset, HexColor } from '@/types';

type TextOverlaySidebarType = {
  allCanvasObjects: fabric.Object[];
  setAllCanvasObjects: (objects: fabric.Object[]) => void;
  selectedObjects: Record<string, fabric.Object>;
  setSelectedObjects: (objects: Record<string, fabric.Object>) => void;
  setHasCanvasChanged: (value: boolean) => void;
  setHasColorChanged: (value: boolean) => void;
  isEditPage: boolean;
};

const TextOverlaySidebar = forwardRef<fabric.Canvas | null, TextOverlaySidebarType>(
  (
    {
      selectedObjects,
      setSelectedObjects,
      allCanvasObjects,
      setAllCanvasObjects,
      setHasCanvasChanged,
      setHasColorChanged,
      isEditPage
    },
    canvasRef
  ) => {
    const { t } = useTranslation('common');

    const [isOpenAddPhotoModal, setIsOpenAddPhotoModal] = useState(false);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [isOpenBackgroundAssetModal, setIsOpenBackgroundAssetModal] = useState(false);
    const [hasBackgroundImage, setHasBackgroundImage] = useState(false);

    useEffect(() => {
      if (!isEditPage) {
        addText();
        setHasCanvasChanged(true);
      }
    }, []);

    useEffect(() => {
      const canvas = getCanvasFromRef(canvasRef);
      setHasBackgroundImage(Boolean(canvas?.backgroundImage));
    }, [canvasRef, allCanvasObjects]);

    const handleDragStart = (index: number) => {
      setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
    };

    const handleDrop = (index: number) => {
      if (draggedIndex === null || draggedIndex === index) {
        return;
      }

      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas) {
        throw new Error('Canvas not initialized');
      }

      const backgroundColor = canvas.backgroundColor;
      const backgroundImage = canvas.backgroundImage;

      const updatedObjects = [...allCanvasObjects];
      const [moved] = updatedObjects.splice(draggedIndex, 1);
      updatedObjects.splice(index, 0, moved);

      canvas.clear();

      const restoreObjects = () => {
        updatedObjects.forEach((obj) => canvas.add(obj));
        canvas.renderAll();
      };

      if (backgroundImage) {
        canvas.setBackgroundImage(backgroundImage, () => {
          if (backgroundColor) {
            canvas.setBackgroundColor(backgroundColor as string, restoreObjects);
            return;
          }

          restoreObjects();
        });
      } else if (backgroundColor) {
        canvas.setBackgroundColor(backgroundColor as string, restoreObjects);
      } else {
        restoreObjects();
      }

      setHasCanvasChanged(true);
      setAllCanvasObjects(updatedObjects);
      setDraggedIndex(null);
    };

    const bringTextObjectsToFront = (canvas: fabric.Canvas) => {
      const allObjects = canvas.getObjects();
      const textObjects = allObjects.filter((obj) => obj.type === CANVAS_OBJECT_TYPE.TEXTBOX);

      textObjects.forEach((textObj) => {
        canvas.bringToFront(textObj);
      });

      canvas.renderAll();
    };

    const addText = () => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas) {
        throw new Error('Canvas not initialized');
      }
      const id = uuidv4();
      const newText = new fabric.Textbox(t('newText'), {
        left: DEFAULT_CANVAS_SIZE.width / 2 - DEFAULT_CANVAS_TEXTBOX_WIDTH / 2,
        top: DEFAULT_CANVAS_SIZE.height / 2,
        data: { id: id },
        fontSize: DEFAULT_CANVAS_FONT_SIZE,
        width: DEFAULT_CANVAS_TEXTBOX_WIDTH,
        textAlign: ALIGNMENT.CENTER,
        fill: DEFAULT_CANVAS_FONT_COLOR,
        fontFamily: fonts[0].value
      });
      canvas.add(newText);
      canvas.setActiveObject(newText);
      setSelectedObjects({ [id]: newText });

      bringTextObjectsToFront(canvas);

      setAllCanvasObjects(canvas.getObjects());
    };

    const removeObjectById = (id: string) => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas) {
        throw new Error('Canvas not initialized');
      }

      if (selectedObjects[id]) {
        const updatedSelectedObjects = { ...selectedObjects };
        delete updatedSelectedObjects[id];
        setSelectedObjects(updatedSelectedObjects);
      }

      const objectToRemove = canvas.getObjects().find((obj) => obj.data?.id === id);

      if (objectToRemove) {
        canvas.remove(objectToRemove);
        canvas.renderAll();
        setHasCanvasChanged(true);
        setAllCanvasObjects(canvas.getObjects());
      }
    };

    const BUTTON_CONTROLS = [
      {
        isSelected: false,
        icon: () => GalleryAddIcon({ className: 'size-6' }),
        handleItemClick: () => {
          setIsOpenBackgroundAssetModal(false);
          setIsOpenAddPhotoModal(true);
        }
      },
      {
        isSelected: false,
        icon: () => TextAddIcon({ className: 'size-6' }),
        handleItemClick: addText
      },
      {
        isSelected: false,
        icon: () => PhotoFilmIcon({ className: 'w-6 text-primary-blue' }),
        handleItemClick: () => {
          setIsOpenBackgroundAssetModal(true);
          setIsOpenAddPhotoModal(true);
        }
      }
    ];

    const selectObject = (canvasObject: fabric.Object) => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas) {
        return;
      }

      canvas.discardActiveObject();

      canvas.setActiveObject(canvasObject);
      canvas.renderAll();

      const newSelectedObjects: Record<string, fabric.Object> = {};
      newSelectedObjects[canvasObject.data?.id] = canvasObject;
      setSelectedObjects(newSelectedObjects);
    };

    const selectAllObjects = (select: boolean) => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas) {
        throw new Error('Canvas not initialized');
      }

      canvas.discardActiveObject();
      const allObjects = canvas.getObjects();

      if (select) {
        const sel = new fabric.ActiveSelection(allObjects, {
          canvas: canvas
        });
        canvas.setActiveObject(sel);

        const newSelectedObjects: Record<string, fabric.Object> = {};
        allObjects.forEach((obj) => {
          newSelectedObjects[obj.data?.id] = obj;
        });
        setSelectedObjects(newSelectedObjects);
      } else {
        canvas.discardActiveObject();
        setSelectedObjects({});
      }

      canvas.requestRenderAll();
    };

    const getObjectLabel = (obj: fabric.Object) => {
      if (obj.type === CANVAS_OBJECT_TYPE.TEXTBOX) {
        const selectedObject = selectedObjects[obj.data?.id] as fabric.Textbox;
        if (selectedObject) {
          return selectedObject.text;
        }
        return (obj as fabric.Textbox).text || CANVAS_OBJECT_TYPE.TEXTBOX;
      }
      return obj.type;
    };

    const addImageToCanvas = (imageAsset: Asset) => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas || !imageAsset?.assetUrl) {
        return;
      }
      fabric.Image.fromURL(
        imageAsset.assetUrl,
        (img) => {
          const scaleFactor = getCanvasImageScale(
            canvas,
            img.width as number,
            img.height as number
          );
          img.set({
            left: DEFAULT_CANVAS_SIZE.width / 2 - ((img.width ?? 0) * scaleFactor) / 2,
            top: DEFAULT_CANVAS_SIZE.height / 2 - ((img.height ?? 0) * scaleFactor) / 2,
            data: { id: uuidv4() },
            crossOrigin: 'anonymous'
          });
          img.scale(scaleFactor);
          canvas.add(img);
          canvas.setActiveObject(img);

          bringTextObjectsToFront(canvas);

          setAllCanvasObjects(canvas.getObjects());
        },
        { crossOrigin: 'anonymous' }
      );
      setHasCanvasChanged(true);
      setIsOpenAddPhotoModal(false);
    };

    const addBackgroundImageToCanvas = (imageAsset: Asset) => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas || !imageAsset?.assetUrl) {
        return;
      }

      setCanvasBackgroundImage(canvas, imageAsset.assetUrl, () => {
        setHasCanvasChanged(true);
        setHasBackgroundImage(true);
        setIsOpenAddPhotoModal(false);
      });
    };

    const handleColorChange = (newColor: HexColor) => {
      const currentColor = getCanvasFromRef(canvasRef)?.backgroundColor as HexColor;
      if (currentColor !== newColor) {
        getCanvasFromRef(canvasRef)?.setBackgroundColor(newColor, () => {
          getCanvasFromRef(canvasRef)?.renderAll();
        });
        setHasCanvasChanged(true);
        setHasColorChanged(true);
      }
    };

    const removeBackgroundImage = () => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas?.backgroundImage) {
        return;
      }

      canvas.setBackgroundImage(null as unknown as fabric.Image, () => {
        canvas.requestRenderAll();
      });
      setHasCanvasChanged(true);
      setHasBackgroundImage(false);
    };

    const isSelectedAll =
      Object.keys(selectedObjects).length > 0 &&
      Object.keys(selectedObjects).length === getCanvasFromRef(canvasRef)?.getObjects().length;

    return (
      <>
        <div className='flex size-full min-h-0 flex-col rounded-xl border border-blue-powder'>
          <div className='flex items-center justify-between rounded-t-xl border-b border-blue-powder bg-ghost-white p-3'>
            <div className='flex'>
              <div className='flex items-center gap-2'>
                {BUTTON_CONTROLS.map((item, index) => (
                  <Toolbar items={[item]} key={index} />
                ))}
              </div>
            </div>
            <div className='rounded-md border border-blue-powder p-px'>
              <div
                className={clsx(
                  'flex h-7 cursor-pointer select-none items-center justify-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal transition-all duration-300',
                  isSelectedAll ? 'bg-blue-powder text-black' : 'text-slate-gray',
                  checkIsCanvasEmpty(canvasRef) && 'pointer-events-none opacity-70'
                )}
                onClick={() => selectAllObjects(!isSelectedAll)}>
                <DoubleCursorIcon className='size-4' />
                <div>{t('selectAll')}</div>
              </div>
            </div>
          </div>
          <div className='flex h-full flex-col'>
            <div className='flex min-h-0 flex-1 flex-col p-3 md:basis-0'>
              <Label text={t('fixed')} className='ml-2' />
              {!hasBackgroundImage && (
                <div className='m-2 flex items-center gap-2 rounded-md border border-blue-powder'>
                  <ColorPicker
                    color={getCanvasFromRef(canvasRef)?.backgroundColor as HexColor}
                    isOpen={isColorPickerOpen}
                    onChange={handleColorChange}
                    className='scale-90'
                    setIsOpen={setIsColorPickerOpen}
                    position={POSITION.LEFT}
                  />
                  <div>
                    <Label text={t('backgroundColor')} />
                  </div>
                </div>
              )}
              {hasBackgroundImage && (
                <div className='m-2 flex items-center justify-between gap-2.5 rounded-md border border-blue-powder bg-white text-slate-gray transition-all duration-300'>
                  <div className='border-r border-blue-powder p-2 transition-all duration-300'>
                    <ImageIcon />
                  </div>
                  <div className='flex-1 truncate rounded-r-md'>{t('background')}</div>
                  <div
                    className='cursor-pointer pr-1.5'
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBackgroundImage();
                    }}>
                    <DeleteIcon className='fill-none stroke-slate-gray stroke-[0.5]' />
                  </div>
                </div>
              )}

              <Label text={t('relative')} className='ml-2' />
              <div className='mt-2 min-h-0 flex-1 overflow-y-auto'>
                {allCanvasObjects.map((canvasObject, index) => {
                  const isSelected = selectedObjects[canvasObject.data?.id];
                  return (
                    <div
                      key={canvasObject.data?.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                      onClick={() => {
                        selectObject(canvasObject);
                      }}
                      className={clsx(
                        'm-2 flex cursor-pointer items-center justify-between gap-2.5 rounded-md border border-blue-powder transition-all duration-300',
                        isSelected ? 'bg-aliceblue text-black' : 'bg-white text-slate-gray'
                      )}>
                      <div
                        className={clsx(
                          'border-r border-blue-powder p-2 transition-all duration-300',
                          isSelected ? 'bg-blue-powder' : 'bg-transparent'
                        )}>
                        {canvasObject.type === CANVAS_OBJECT_TYPE.TEXTBOX ? (
                          <CanvasTextIcon />
                        ) : (
                          <ImageIcon />
                        )}
                      </div>
                      <div className={'flex-1 truncate rounded-r-md'}>
                        {getObjectLabel(canvasObject)}
                      </div>
                      <div
                        className='pr-1.5'
                        onClick={(e) => {
                          e.stopPropagation();
                          removeObjectById(canvasObject.data?.id);
                        }}>
                        <DeleteIcon className='fill-none stroke-slate-gray stroke-[0.5]' />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {isOpenAddPhotoModal && (
          <AddPhotosModal
            onClose={() => setIsOpenAddPhotoModal(false)}
            onSubmit={(asset) => {
              if (isOpenBackgroundAssetModal) {
                addBackgroundImageToCanvas(asset);
                return;
              }
              addImageToCanvas(asset);
            }}
            isOpen={isOpenAddPhotoModal}
            modalForBackgroundLibrary={isOpenBackgroundAssetModal}
          />
        )}
      </>
    );
  }
);

TextOverlaySidebar.displayName = 'TextOverlaySidebar';

export { TextOverlaySidebar };
